import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import '../common/commonStyles.css';

const PurchaseOrderReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [reportDate, setReportDate] = useState('');
  const [inventoryReport, setInventoryReport] = useState([]);
  const [footerTotals, setFooterTotals] = useState({
    total_cost: '0.00',
    total_price: '0.00',
    total_purchase_box_qty: '0'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'dd/MM/yyyy');
      const formattedEndDate = format(value[1], 'dd/MM/yyyy');
      setReportrange(formattedStartDate + ' - ' + formattedEndDate);
      setPage(0);
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('draw', 1);
      formData.append('start', page * rowsPerPage);
      formData.append('length', rowsPerPage);
      formData.append('searchValue', searchQuery);
      formData.append('reportrange', reportrange);

      const response = await fetch(`${config.API_URL}/report/purchase_order_report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const json = await response.json();

      if (json && json.data && Array.isArray(json.data)) {
        setInventoryReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
      }

      if (json && json.totals) {
        setFooterTotals({
          total_cost: json.totals.total_cost || '0.00',
          total_price: json.totals.total_price || '0.00',
          total_purchase_box_qty: json.totals.total_purchase_box_qty || '0'
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken,token, page, rowsPerPage, reportrange, searchQuery]);

  useEffect(() => {
    if (reportrange) {
      fetchReport();
    }
  }, [reportrange, page, rowsPerPage, searchQuery, fetchReport]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = async () => {
    const formData = new FormData();
    formData.append('draw', '1');
    formData.append('start', '0');
    formData.append("length", totalCount || 10000);
    formData.append('searchValue', searchQuery);
    formData.append('reportrange', reportrange);

    try {
      const response = await fetch(`${config.API_URL}/report/purchase_order_report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      const exportData = (data.data || []).map(row => ({
        Code: row.code || '',
        Name: row.name || '',
        Type: row.type === "1" ? "BOX" : "PIECE",
        Cost: row.cost || 0,
        Price: row.price || 0,
        "Purchase Box Qty": row.purchase_box_qty || 0
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "PurchaseOrderReport");
      XLSX.writeFile(wb, "PurchaseOrderReport.xlsx");
    } catch (error) {
    }
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Typography className="page-title">
                  Purchase Order Report
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  className="input-border"
                  placeholder="Search by code, name..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                              setPage(0);
                            }}
                            sx={{ color: "#637381" }}
                          >
                            <Iconify icon="eva:close-fill" width={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  showOneCalendar
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 2 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  className="button-variant-outlined"
                  variant="outlined"
                  startIcon={<Iconify icon="eva:download-outline" width={20} />}
                  onClick={exportToExcel}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Export to Excel
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell
                      sx={{
                        width: "100px",
                      }}
                    >
                      Code
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "200px",
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "100px",
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "120px",
                        textAlign: "right",
                      }}
                    >
                      Cost
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "120px",
                        textAlign: "right",
                      }}
                    >
                      Price
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "150px",
                        textAlign: "right",
                      }}
                    >
                      Purchase Box Qty
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventoryReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: "#0e0f0fef" }}>
                          {reportrange ? "No data available" : "Please select a date range"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    inventoryReport.map((row, index) => (
                      <TableRow
                        key={index}
                        hover
                        className="table-cell-style"
                      >
                        <TableCell
                        >
                          {row.code || ""}
                        </TableCell>
                        <TableCell
                        >
                          {row.name || ""}
                        </TableCell>
                        <TableCell
                        >
                          {row.type === "1" ? "BOX" : "PIECE"}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "120px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumber(row.cost)}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "120px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumber(row.price)}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "150px",
                            textAlign: "right",
                          }}
                        >
                          {row.purchase_box_qty || "0"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        fontSize: '0.8rem',
                        color: '#212b36',
                        fontWeight: 600,
                        py: 1.2,
                        borderBottom: "none",
                      }
                    }}
                  >
                    <TableCell colSpan={2} sx={{ borderBottom: "none" }} />
                    <TableCell
                    >
                      Total
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                      }}
                    >
                      {formatNumber(footerTotals.total_cost)}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "120px",
                        textAlign: "right",
                      }}
                    >
                      {formatNumber(footerTotals.total_price)}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: "150px",
                        textAlign: "right",
                      }}
                    >
                      {footerTotals.total_purchase_box_qty}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}

          {!loading && inventoryReport.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default PurchaseOrderReport;
